import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AuditTrail = ({ document, onClose, onExportReport }) => {
  const [filterType, setFilterType] = useState('all'); // all, signatures, views, comments, changes

  const auditEvents = [
    {
      id: 1,
      type: 'signature',
      action: 'Document Signed',
      user: 'Michael Rodriguez',
      role: 'Solution Architect',
      timestamp: '2024-10-10T14:30:00Z',
      ipAddress: '192.168.1.100',
      details: 'Digital signature applied using typed method',
      metadata: {
        signatureType: 'typed',
        authMethod: 'MFA',
        browser: 'Chrome 118.0'
      }
    },
    {
      id: 2,
      type: 'view',
      action: 'Document Viewed',
      user: 'Sarah Chen',
      role: 'Business Analyst',
      timestamp: '2024-10-10T13:45:00Z',
      ipAddress: '192.168.1.101',
      details: 'Document opened for review',
      metadata: {
        duration: '12 minutes',
        pagesViewed: '1-8',
        browser: 'Firefox 119.0'
      }
    },
    {
      id: 3,
      type: 'comment',
      action: 'Comment Added',
      user: 'David Kim',
      role: 'Technical Lead',
      timestamp: '2024-10-10T12:20:00Z',
      ipAddress: '192.168.1.102',
      details: 'Added comment on page 3 regarding integration requirements',
      metadata: {
        page: 3,
        commentLength: 156,
        browser: 'Safari 17.0'
      }
    },
    {
      id: 4,
      type: 'change',
      action: 'Document Updated',
      user: 'System',
      role: 'Automated Process',
      timestamp: '2024-10-10T11:15:00Z',
      ipAddress: '10.0.0.1',
      details: 'Version 1.2 generated with updated requirements',
      metadata: {
        previousVersion: '1.1',
        newVersion: '1.2',
        changesCount: 7
      }
    },
    {
      id: 5,
      type: 'delegation',
      action: 'Approval Delegated',
      user: 'Jennifer Walsh',
      role: 'Project Manager',
      timestamp: '2024-10-10T10:30:00Z',
      ipAddress: '192.168.1.103',
      details: 'Delegated approval authority to Michael Rodriguez',
      metadata: {
        delegatedTo: 'Michael Rodriguez',
        reason: 'Out of office',
        expiryDate: '2024-10-15'
      }
    },
    {
      id: 6,
      type: 'view',
      action: 'Document Accessed',
      user: 'Robert Johnson',
      role: 'Enterprise Architect',
      timestamp: '2024-10-10T09:45:00Z',
      ipAddress: '192.168.1.104',
      details: 'Initial document review and validation',
      metadata: {
        duration: '25 minutes',
        pagesViewed: 'All',
        browser: 'Edge 118.0'
      }
    }
  ];

  const getEventIcon = (type) => {
    switch (type) {
      case 'signature': return 'PenTool';
      case 'view': return 'Eye';
      case 'comment': return 'MessageSquare';
      case 'change': return 'Edit';
      case 'delegation': return 'UserCheck';
      default: return 'Activity';
    }
  };

  const getEventColor = (type) => {
    switch (type) {
      case 'signature': return 'text-success bg-success/10 border-success/20';
      case 'view': return 'text-primary bg-primary/10 border-primary/20';
      case 'comment': return 'text-warning bg-warning/10 border-warning/20';
      case 'change': return 'text-accent bg-accent/10 border-accent/20';
      case 'delegation': return 'text-secondary bg-secondary/10 border-secondary/20';
      default: return 'text-muted-foreground bg-muted border-border';
    }
  };

  const filteredEvents = auditEvents?.filter(event => 
    filterType === 'all' || event?.type === filterType
  );

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return {
      date: date?.toLocaleDateString(),
      time: date?.toLocaleTimeString()
    };
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon name="Shield" size={24} className="text-primary" />
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                Audit Trail
              </h2>
              <p className="text-sm text-muted-foreground">
                {document?.title} - Complete activity history
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              onClick={() => onExportReport(document?.id)}
            >
              Export Report
            </Button>
            <Button
              variant="ghost"
              size="icon"
              iconName="X"
              onClick={onClose}
            />
          </div>
        </div>

        {/* Filters */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-foreground">Filter by:</span>
            <div className="flex space-x-2">
              {[
                { value: 'all', label: 'All Events' },
                { value: 'signature', label: 'Signatures' },
                { value: 'view', label: 'Views' },
                { value: 'comment', label: 'Comments' },
                { value: 'change', label: 'Changes' }
              ]?.map((filter) => (
                <button
                  key={filter?.value}
                  onClick={() => setFilterType(filter?.value)}
                  className={`px-3 py-1 rounded-full text-xs font-medium nav-transition ${
                    filterType === filter?.value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {filter?.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Audit Events */}
        <div className="flex-1 overflow-auto p-6">
          <div className="space-y-4">
            {filteredEvents?.map((event, index) => {
              const { date, time } = formatTimestamp(event?.timestamp);
              
              return (
                <div key={event?.id} className="relative">
                  {/* Timeline Line */}
                  {index < filteredEvents?.length - 1 && (
                    <div className="absolute left-6 top-12 w-0.5 h-16 bg-border" />
                  )}
                  <div className="flex space-x-4">
                    {/* Event Icon */}
                    <div className={`w-12 h-12 rounded-full border-2 flex items-center justify-center ${getEventColor(event?.type)}`}>
                      <Icon 
                        name={getEventIcon(event?.type)} 
                        size={20} 
                        className="text-current"
                      />
                    </div>

                    {/* Event Details */}
                    <div className="flex-1 bg-card border border-border rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-sm font-semibold text-foreground">
                            {event?.action}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {event?.details}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">{date}</p>
                          <p className="text-xs text-muted-foreground">{time}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                        <div>
                          <p className="text-xs text-muted-foreground">User</p>
                          <p className="text-sm font-medium text-foreground">
                            {event?.user}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {event?.role}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">IP Address</p>
                          <p className="text-sm font-medium text-foreground">
                            {event?.ipAddress}
                          </p>
                        </div>
                        {event?.metadata?.browser && (
                          <div>
                            <p className="text-xs text-muted-foreground">Browser</p>
                            <p className="text-sm font-medium text-foreground">
                              {event?.metadata?.browser}
                            </p>
                          </div>
                        )}
                        {event?.metadata?.authMethod && (
                          <div>
                            <p className="text-xs text-muted-foreground">Auth Method</p>
                            <p className="text-sm font-medium text-foreground">
                              {event?.metadata?.authMethod}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Additional Metadata */}
                      {Object.keys(event?.metadata)?.length > 0 && (
                        <div className="pt-3 border-t border-border">
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                            {Object.entries(event?.metadata)?.map(([key, value]) => (
                              <div key={key}>
                                <span className="text-muted-foreground capitalize">
                                  {key?.replace(/([A-Z])/g, ' $1')?.trim()}:
                                </span>
                                <span className="ml-1 text-foreground font-medium">
                                  {value}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30">
          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
            <div className="flex items-center space-x-2">
              <Icon name="Shield" size={16} />
              <span>Tamper-proof audit log</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Clock" size={16} />
              <span>Real-time tracking</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Lock" size={16} />
              <span>Encrypted storage</span>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AuditTrail;